import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RoleId } from "./../../app/Enums/RoleId";
import checkTokenExpiration from "./../../app/constants/checkTokenExpiration";
import { toast } from "react-toastify";
import Pagination from "./../../app/components/Pagination/Pagination";
import DeleteModal from "./../../app/components/FeedbackModal/DeleteModal/DeleteModal";
import {
  getAllNewsletters,
  deleteNewsletter,
  toggleNewsletterStatus,
  sendNewsLettertoRegisteredEmail,
} from "../../api/newsletterApi/newsletter";
import { routePath } from "../../app/routes/routepath";
import { useDispatch } from "react-redux";
import { setSelectedNewsletter } from "../../app/redux/slices/newsletterManagement/newsletterSlice";
import LoadingSpinner from "../../app/components/Loader/LoadingSpinner";
import addSvg from "../../assets/icons/add.svg";
import viewSvg from "../../assets/icons/view.svg";
import binSvg from "../../assets/icons/bin.svg";
import editSvg from "../../assets/icons/edit.svg";
import "./AdminNewsletters.css";
import {
  Ban,
  CheckCircle,
  Eye,
  Lock,
  MailCheck,
  MailX,
  SquarePen,
  Trash2,
  Unlock,
  UserX,
  Loader,
} from "lucide-react";

function AdminNewsletters() {
  const navigate = useNavigate();

  const [newsletterList, setNewsletterList] = useState([]);
  const [PageIndex, setPageIndex] = useState(1);
  const [PageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [roleId, setRole] = useState(0);
  const [authId, setAuthId] = useState(0);
  const totalPages = Math.ceil(totalRows / PageSize);
  const [selectedOption, setSelectedOption] = useState(0);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const tableRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState("");
  
  // State for handling email sending loading states
  const [sendingEmails, setSendingEmails] = useState(new Set());

  useEffect(() => {
    checkTokenExpiration();
    const roleId = JSON.parse(localStorage.getItem("RoleId"));
    const authId = JSON.parse(localStorage.getItem("UserId"));
    setIsSuperAdmin(roleId === RoleId.SuperAdmin);
    setIsAdmin(roleId === RoleId.Admin);
    setRole(roleId);
    setAuthId(authId);
    if (roleId === RoleId.Admin) {
      setSelectedOption(2);
    } else if (roleId === RoleId.SuperAdmin) {
      setSelectedOption(1);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin || isAdmin) {
      fetchNewsletters();
    }
  }, [isSuperAdmin, isAdmin, PageIndex, PageSize, selectedOption]);

  useEffect(() => {
    tableRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [PageSize, PageIndex]);

  const navigateDashboard = () => {
    navigate("/dashboard");
  };

  const fetchNewsletters = async () => {
    try {
      const data = {
        Filter: {
          AuthorId: authId,
          Filter: selectedOption,
          UserRoleId: roleId,
        },
        PageIndex,
        PageSize,
      };

      const res = await getAllNewsletters(data);

      if (res.data.result === 1) {
        const resultList = res.data.result_value;
        console.log(res.data);

        setNewsletterList(resultList);
        setTotalRows(
          resultList.length > 0 ? parseInt(resultList[0]?.total_rows || 0) : 0,
        );
        setIsLoading(false);
      } else {
        toast.warning(res.data.result_value?.sp_message || "No data found");
        setNewsletterList([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error fetching newsletters:", error);
      toast.error("Something went wrong while fetching data.");
    }
  };

  const formatDateIST = (dateString) => {
    const options = {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
    };
    return new Intl.DateTimeFormat("en-IN", options).format(
      new Date(dateString),
    );
  };

  const confirmDelete = (id) => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      const res = await deleteNewsletter(selectedId);
      if (!res.error) {
        toast.success(res.data?.error_value.errorText);
        fetchNewsletters();
      } else {
        toast.error(res.data?.error_value.errorText);
      }
    } catch (error) {
      toast.error("Something went wrong while deleting.");
    } finally {
      setShowDeleteModal(false);
      setSelectedId(null);
    }
  };

  const changeActivation = async (id) => {
    try {
      const res = await toggleNewsletterStatus(id);
      if (!res.error) {
        toast.success(res.data?.error_value.errorText);
        fetchNewsletters();
      }
    } catch (error) {
      toast.error("Something went wrong while deleting");
    }
  };

  const handleNavigation = (id) => {
    dispatch(setSelectedNewsletter(id));
    navigate(routePath.editNewsletter);
  };
  
  const handleView = (id) => {
    dispatch(setSelectedNewsletter(id));
    navigate(routePath.viewNewsletter);
  };

  const handleChange = (e) => {
    var selectedValue = e.target.value;
    setSelectedOption(selectedValue);
  };

  const sendNewsLettertoEmail = async (newsletter_id) => {
    // Prevent multiple clicks on the same newsletter
    if (sendingEmails.has(newsletter_id)) {
      return;
    }
    
    // Add to sending set
    setSendingEmails(prev => new Set(prev).add(newsletter_id));
    
    try {
      const res = await sendNewsLettertoRegisteredEmail(newsletter_id);
      if (res.data.result === 1) {
        fetchNewsletters();
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error("Something went wrong while sending to email.");
    } finally {
      // Remove from sending set
      setSendingEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(newsletter_id);
        return newSet;
      });
    }
  };

  if (!isSuperAdmin && !isAdmin) {
    return (
      <div className="no-access">
        <h3>You are not authorized to view this page.</h3>
      </div>
    );
  }

  const openModal = (description) => {
    setSelectedDescription(description);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDescription("");
  };

  return (
    <>
      <div className="main-out-wraper">
        <div className="role-box">
          <h2 className={isAdmin ? "" : "mb-0"}>Admin Newsletters</h2>
          {isAdmin && (
            <div className="search-filter-section">
              <div>
                <button
                  type="button"
                  className="primary_btn"
                  onClick={() => navigate("/addAdminNewsletter")}
                >
                  <img src={addSvg} alt="Add" />
                  Add Newsletter
                </button>
              </div>
              <div></div>
            </div>
          )}
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className="main_widget">
            <div className="main_widget_body">
              <div className="table-responsive" ref={tableRef}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ minWidth: "47px" }}>S. No</th>
                      <th style={{ minWidth: "430px" }}>Title</th>
                      <th style={{ minWidth: "180px" }}>Cover image</th>
                      <th style={{ minWidth: "430px" }}>Summary</th>
                      <th style={{ minWidth: "420px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newsletterList.length > 0 ? (
                      newsletterList.map((newsletter, index) => (
                        <tr key={newsletter.newsletter_id}>
                          <td>{(PageIndex - 1) * PageSize + index + 1}</td>
                          <td>{newsletter.newsletter_title}</td>
                          <td>
                            {
                              <img
                                src={
                                  newsletter.newsletter_coverImage ||
                                  "https://placehold.co/100x60?text=No\nImage"
                                }
                                alt="cover"
                                className="CoverImage"
                              />
                            }
                          </td>
                          <td style={{ wordBreak: "break-all" }}>
                            {newsletter.newsletter_description.length > 80 ? (
                              <>
                                {newsletter.newsletter_description.slice(0, 80)}
                                ...
                                <span
                                  className="see-more"
                                  onClick={() =>
                                    openModal(newsletter.newsletter_description)
                                  }
                                >
                                  {" "}
                                  See more
                                </span>
                              </>
                            ) : (
                              newsletter.newsletter_description
                            )}
                          </td>
                          <td>
                            {isSuperAdmin && selectedOption == 1 && (
                              <div className="action_buttons">
                                <div
                                  className="tooltip-wrapper"
                                  style={{ "--bg-color": "#002954" }}
                                  onClick={() =>
                                    handleView(newsletter.newsletter_id)
                                  }
                                >
                                  <Eye className="icon icon-view" />
                                  <span className="tooltip-text">
                                    View Newsletter
                                  </span>
                                </div>

                                {newsletter.is_active === 1 ? (
                                  <div
                                    id="EnableVisibility"
                                    className="tooltip-wrapper"
                                    style={{ "--bg-color": "#16a34a" }}
                                  >
                                    <CheckCircle className="icon icon-active" />
                                    <span className="tooltip-text">Active</span>
                                  </div>
                                ) : (
                                  <div
                                    id="DisableVisibility"
                                    className="tooltip-wrapper"
                                    style={{ "--bg-color": "#9ca3af" }}
                                  >
                                    <Ban className="icon icon-inactive" />
                                    <span className="tooltip-text">
                                      Inactive
                                    </span>
                                  </div>
                                )}

                                {newsletter.is_mail_sended == 0 ? (
                                  <div
                                    className="tooltip-wrapper"
                                    style={{ "--bg-color": "#ef4444" }}
                                  >
                                    <MailX className="icon icon-rejected" />
                                    <span className="tooltip-text">
                                      Email not sent
                                    </span>
                                  </div>
                                ) : (
                                  <div
                                    className="tooltip-wrapper"
                                    style={{ "--bg-color": "#22c55e" }}
                                  >
                                    <MailCheck className="icon icon-approved" />
                                    <span className="tooltip-text">
                                      Email already sent
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* for admin */}
                            {isAdmin && selectedOption == 2 && (
                              <div className="action_buttons">
                                <div
                                  className="tooltip-wrapper"
                                  style={{ "--bg-color": "#002954" }}
                                  onClick={() =>
                                    handleView(newsletter.newsletter_id)
                                  }
                                >
                                  <Eye className="icon icon-view" />
                                  <span className="tooltip-text">
                                    View Newsletter
                                  </span>
                                </div>
                                <div
                                  className="tooltip-wrapper"
                                  style={{ "--bg-color": "#dc2626" }}
                                  onClick={() =>
                                    confirmDelete(newsletter.newsletter_id)
                                  }
                                >
                                  <Trash2 className="icon icon-deactivated" />
                                  <span className="tooltip-text">
                                    Delete Newsletter
                                  </span>
                                </div>

                                <div
                                  className="tooltip-wrapper"
                                  style={{ "--bg-color": "#F59E0B" }}
                                  onClick={() =>
                                    handleNavigation(newsletter.newsletter_id)
                                  }
                                >
                                  <SquarePen className="icon icon-btn" />
                                  <span className="tooltip-text">
                                    Edit Newsletter
                                  </span>
                                </div>

                                {newsletter.is_active ? (
                                  <div
                                    className="tooltip-wrapper"
                                    style={{ "--bg-color": "#dc2626" }}
                                    onClick={() =>
                                      changeActivation(newsletter.newsletter_id)
                                    }
                                  >
                                    <Unlock className="icon icon-deactivated" />
                                    <span className="tooltip-text">
                                      Disable
                                    </span>
                                  </div>
                                ) : (
                                  <div
                                    className="tooltip-wrapper"
                                    style={{ "--bg-color": "#22c55e" }}
                                    onClick={() =>
                                      changeActivation(newsletter.newsletter_id)
                                    }
                                  >
                                    <Lock className="icon icon-approved" />
                                    <span className="tooltip-text">Enable</span>
                                  </div>
                                )}
                                
                                {newsletter.is_mail_sended == 0 ? (
                                  <div
                                    className={`tooltip-wrapper ${sendingEmails.has(newsletter.newsletter_id) ? 'sending-disabled' : ''}`}
                                    style={{ 
                                      "--bg-color": sendingEmails.has(newsletter.newsletter_id) ? "#9ca3af" : "#22c55e",
                                      cursor: sendingEmails.has(newsletter.newsletter_id) ? "not-allowed" : "pointer",
                                      opacity: sendingEmails.has(newsletter.newsletter_id) ? 0.6 : 1
                                    }}
                                    onClick={(e) => {
                                      if (!sendingEmails.has(newsletter.newsletter_id)) {
                                        sendNewsLettertoEmail(newsletter.newsletter_id);
                                      }
                                    }}
                                  >
                                    {sendingEmails.has(newsletter.newsletter_id) ? (
                                      <Loader className="icon icon-approved spinning" />
                                    ) : (
                                      <MailCheck className="icon icon-approved" />
                                    )}
                                    <span className="tooltip-text">
                                      {sendingEmails.has(newsletter.newsletter_id) ? "Sending..." : "Send Email"}
                                    </span>
                                  </div>
                                ) : (
                                  <div
                                    className="tooltip-wrapper"
                                    style={{ "--bg-color": "#ef4444" }}
                                    onClick={() =>
                                      toast.error(
                                        "Newsletter already sent to Emails. Thank you",
                                      )
                                    }
                                  >
                                    <MailX className="icon icon-rejected" />
                                    <span className="tooltip-text">
                                      Already Sent
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No data found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        <Pagination
          currentPage={PageIndex}
          totalPages={totalPages}
          PageSize={PageSize}
          onPageChange={setPageIndex}
          onPageSizeChange={setPageSize}
        />

        <DeleteModal
          isOpen={showDeleteModal}
          onConfirm={() => {
            handleDelete();
            setShowDeleteModal(false);
          }}
          onCancel={() => {
            setShowDeleteModal(false);
          }}
          title="Delete Newsletter"
          message={`Are you sure you want to delete this newsletter?`}
        />
      </div>
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Newsletter Summary</h2>
            <p>{selectedDescription}</p>
            <div className="d-flex justify-content-center">
              <button className="primary_btn" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AdminNewsletters;